import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { GardenBackdrop } from '../components/RoundView';
import { playMiniTaskSound } from '../soundEffects';
import { withTurkishGenitive } from '../turkishSuffixes';
import { CHOICE_TONES, CHOICE_FEEDBACK } from '../choicePalette';
import PatternPrompt, { PatternValue } from './PatternPrompt';
import { patternChoiceSequenceLength } from './patternChoiceLayout';
import { PatternSizeContext, usePatternQuestionScale } from './PatternSizeContext';
import { isPositionLinePrompt } from './PatternPosition';
import { mirrorGridInPrompt, patternInstruction } from './PatternMirrorGrid';
import { isPatternAttemptCorrect } from './patternRecognitionState';
import {
  clearPatternRecognitionProgress,
  readPatternRecognitionProgress,
  writePatternRecognitionProgress,
} from './patternRecognitionSessionStorage';

const NEXT_QUESTION_DELAY_MS = 650;

const pageSx = {
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  maxWidth: '100vw',
  bgcolor: '#fffaf0',
  color: '#172554',
  overflowX: 'hidden',
  backgroundImage:
    'radial-gradient(circle at 8% 36%, rgba(255,255,255,0.9) 0 54px, transparent 55px), radial-gradient(circle at 92% 34%, rgba(255,255,255,0.82) 0 52px, transparent 53px), linear-gradient(180deg, #fffaf0 0%, #fff8ea 70%, #f7f4d6 100%)',
};

function choiceButtonSx({ index, selected, correctChoice, revealAnswer, disabled }) {
  const showCorrect = revealAnswer && correctChoice;
  const showWrong = selected && !correctChoice;
  const tone = CHOICE_TONES[index % CHOICE_TONES.length];
  return {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: { xs: 82, sm: 96, md: 112 },
    px: { xs: 1.2, sm: 2 },
    borderRadius: 3,
    border: `4px solid ${showCorrect ? CHOICE_FEEDBACK.correct.border : showWrong ? CHOICE_FEEDBACK.wrong.border : tone.border}`,
    bgcolor: showCorrect ? CHOICE_FEEDBACK.correct.bg : showWrong ? CHOICE_FEEDBACK.wrong.bg : tone.bg,
    color: showCorrect ? CHOICE_FEEDBACK.correct.text : '#191756',
    boxShadow: showCorrect
      ? CHOICE_FEEDBACK.correct.shadow
      : '0 5px 0 rgba(15,23,42,0.08), 0 12px 20px rgba(15,23,42,0.08)',
    opacity: disabled && !showCorrect && !showWrong ? 0.66 : 1,
    textTransform: 'none',
    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
    '&:hover': disabled ? {} : { transform: 'translateY(-2px)', boxShadow: '0 7px 0 rgba(15,23,42,0.08), 0 16px 26px rgba(79,70,229,0.16)' },
    '&:focus-visible': { outline: '4px solid rgba(124,58,237,0.34)', outlineOffset: 2 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none', transform: 'none' },
  };
}

function QuestionCard({ question, attempts, locked, revealAnswer, onChoice, onTarget }) {
  const questionSize = usePatternQuestionScale(question);
  const mirrorGrid = mirrorGridInPrompt(question.prompt);
  const fullReferenceWidth = questionSize.enabled || isPositionLinePrompt(question.prompt);
  const choiceSequenceLength = Math.max(0, ...(question.choices || [])
    .map((choice) => patternChoiceSequenceLength(choice.value)));
  return (
    <PatternSizeContext.Provider value={questionSize}>
    <Box ref={questionSize.rootRef} data-testid="pattern-question-card" data-size-semantics={questionSize.semantics} data-question-scale={questionSize.enabled ? questionSize.scale : undefined} data-size-layout={questionSize.enabled ? questionSize.wrap ? 'wrap' : 'single-row' : undefined} data-size-policy-issues={questionSize.issues.join(',') || undefined} sx={{ width: '100%', minWidth: 0, maxWidth: 1260, mx: 'auto', display: 'grid', gap: { xs: 1.4, sm: 2 } }}>
      <Typography component="h2" sx={{ textAlign: 'center', fontWeight: 1000, color: '#191756', fontSize: { xs: 24, sm: 30, md: 38 }, lineHeight: 1.08 }}>
        {patternInstruction(question)}
      </Typography>
      <Box data-pattern-size-container="reference" sx={{ width: fullReferenceWidth ? '100%' : undefined, minWidth: 0, boxSizing: 'border-box', minHeight: { xs: 122, sm: 158 }, display: 'grid', gridTemplateColumns: fullReferenceWidth ? 'minmax(0, 1fr)' : undefined, placeItems: 'center', p: { xs: 1, sm: 1.8 }, borderRadius: 4 }}>
        <PatternPrompt
          prompt={question.prompt}
          direct={question.interaction === 'direct_select'}
          attempts={attempts}
          locked={locked}
          revealAnswer={revealAnswer}
          correctTargetIndex={question.correct_target_index}
          onSelect={onTarget}
        />
      </Box>
      {question.interaction === 'choice' ? (
        <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: choiceSequenceLength >= 7 ? '1fr' : 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 1.2, md: 2 } }}>
          {(question.choices || []).map((choice, index) => {
            const selected = attempts.some((item) => item.choice_id === choice.choice_id);
            return (
              <Button
                key={choice.choice_id}
                type="button"
                aria-label={`${choice.choice_id.toUpperCase()} seçeneği`}
                data-pattern-size-container="choice"
                disabled={locked}
                disableRipple={Boolean(mirrorGrid)}
                onClick={() => onChoice(choice.choice_id)}
                sx={{
                  ...choiceButtonSx({
                    index,
                    selected,
                    correctChoice: choice.choice_id === question.correct_choice_id,
                    revealAnswer,
                    disabled: locked,
                  }),
                  ...(mirrorGrid ? { py: 1, '&:focus-visible': { outline: '4px solid #6d28d9', outlineOffset: 2 } } : {}),
                }}
              >
                {mirrorGrid ? <Box component="span" aria-hidden="true" sx={{ position: 'absolute', left: 12, color: '#33258a', fontSize: { xs: 18, md: 24 }, fontWeight: 900 }}>{choice.choice_id.toUpperCase()}</Box> : null}
                <PatternValue value={choice.value} variant={mirrorGrid ? 'mirror_choice' : 'choice'} choiceSequenceLength={choiceSequenceLength} />
              </Button>
            );
          })}
        </Box>
      ) : null}
    </Box>
    </PatternSizeContext.Provider>
  );
}

function normalizedResponses(questions, responses) {
  return questions.map((question, index) => responses[index] || {
    question_id: question.question_id,
    attempts: [],
  });
}

function serverResponses(responses) {
  return responses.map((response) => ({
    question_id: response.question_id,
    attempts: response.attempts.map(({ correct: _correct, ...attempt }) => attempt),
  }));
}

export function PatternRecognitionQaView({ question, classTitle, position, questionCount, onExit }) {
  const attempts = question?.interaction === 'choice'
    ? [{ choice_id: question.correct_choice_id }]
    : [{ target_index: question?.correct_target_index }];

  return (
    <Box sx={pageSx}>
      <Box sx={{ position: 'fixed', bottom: 12, left: 12, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.5, py: 0.5, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>İÇERİK QA</Box>
      <GardenBackdrop />
      <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', width: '100%', maxWidth: 1720, boxSizing: 'border-box', px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 2, md: 3 } }}>
        <MiniTaskGameHeader classTitle={classTitle} soundEnabled={false} onToggleSound={() => {}} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '220px 1fr 220px' }, alignItems: 'center', gap: { xs: 1.2, md: 2 }, mt: { xs: 2, md: 4 }, mb: { xs: 3.2, sm: 1.5, md: 2.4 } }}>
          <ButtonBase onClick={onExit} sx={{ justifySelf: { xs: 'start', md: 'center' }, gridColumn: { xs: '1', md: 'auto' }, gridRow: { xs: '1', md: 'auto' }, borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.86)', color: '#33258a', px: { xs: 1.5, md: 2.2 }, py: { xs: 0.9, md: 1.2 }, minWidth: { xs: 146, md: 210 }, minHeight: { xs: 52, md: 60 }, boxShadow: '0 4px 0 rgba(91,33,182,0.10)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 25, md: 31 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 25 }, lineHeight: 1 }}>Seçime dön</Typography>
          </ButtonBase>
          <Typography sx={{ textAlign: 'center', gridColumn: { xs: '1 / -1', md: 'auto' }, gridRow: { xs: '2', md: 'auto' }, color: '#251777', fontWeight: 1000, fontSize: { xs: 28, sm: 42, md: 54 }, lineHeight: 1, textShadow: '0 3px 0 rgba(255,255,255,0.94)' }}>Öğretmenin Mini Görevi</Typography>
          <Box sx={{ justifySelf: { xs: 'end', md: 'center' }, gridColumn: { xs: '2', md: 'auto' }, gridRow: { xs: '1', md: 'auto' }, borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.86)', color: '#33258a', px: { xs: 1.5, md: 2.2 }, py: { xs: 0.9, md: 1.2 }, minWidth: { xs: 118, md: 210 }, textAlign: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 25 }, lineHeight: 1 }}>Soru {position} / {questionCount}</Typography>
            <Typography sx={{ mt: 0.45, fontWeight: 900, fontSize: { xs: 14, md: 18 } }}>Cevap gösteriliyor</Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%', maxWidth: { xs: 'calc(100vw - 20px)', sm: 1260 }, boxSizing: 'border-box', mx: 'auto', position: 'relative', borderRadius: 4, bgcolor: 'rgba(255,255,255,0.94)', border: '2px solid #e9d5ff', boxShadow: '0 15px 35px rgba(76,29,149,0.14), inset 0 0 0 2px rgba(255,255,255,0.84)', px: { xs: 1.4, sm: 2.4, md: 5 }, pt: { xs: 4.5, md: 5.6 }, pb: { xs: 1.6, md: 2.4 } }}>
          <Box sx={{ position: 'absolute', left: '50%', top: 0, transform: 'translate(-50%, -50%)', borderRadius: 2.5, border: '2px solid #d8b4fe', bgcolor: '#f5f0ff', color: '#6d45df', px: { xs: 1.4, md: 2.2 }, py: { xs: 0.55, md: 0.75 }, display: 'flex', alignItems: 'center', gap: 0.8, minWidth: { xs: 190, md: 270 }, justifyContent: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 25, md: 31 }, filter: 'drop-shadow(0 2px 0 #f59e0b)' }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 23 }, lineHeight: 1 }}>Deseni Yakala</Typography>
          </Box>
          {question ? <QuestionCard question={question} attempts={attempts} locked revealAnswer onChoice={() => {}} onTarget={() => {}} /> : null}
        </Box>
      </Box>
    </Box>
  );
}

export default function PatternRecognitionRoundView({
  round,
  boardSessionId,
  classTitle,
  soundEnabled,
  onToggleSound,
  onExit,
  onComplete,
}) {
  const questions = useMemo(() => round?.questions || [], [round]);
  const durationMs = Math.max(1, Number(round?.duration_seconds || 120)) * 1000;
  const stored = useMemo(
    () => readPatternRecognitionProgress(round?.round_id, boardSessionId),
    [boardSessionId, round?.round_id],
  );
  const canResumeStoredRound = Number(stored?.stage_index || 0) === 0 && stored?.phase !== 'between_rounds';
  const initialPendingRounds = Array.isArray(stored?.pending_rounds) ? stored.pending_rounds.slice(0, 1) : [];
  const [questionIndex, setQuestionIndex] = useState(() => (
    canResumeStoredRound ? Math.min(Number(stored?.question_index || 0), 4) : 0
  ));
  const [responses, setResponses] = useState(() => (
    canResumeStoredRound && Array.isArray(stored?.responses) ? stored.responses : []
  ));
  const [pendingRounds, setPendingRounds] = useState(initialPendingRounds);
  const [practiceCompleted, setPracticeCompleted] = useState(() => Boolean(stored?.practice_completed));
  const [practiceAttempts, setPracticeAttempts] = useState([]);
  const [locked, setLocked] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [elapsedMs, setElapsedMs] = useState(() => (
    canResumeStoredRound ? Math.min(durationMs, Number(stored?.elapsed_ms || 0)) : 0
  ));
  const [phase, setPhase] = useState(() => {
    if (initialPendingRounds.length) return 'submit_error';
    if (stored?.phase === 'practice') return 'practice';
    if (!stored && round?.show_practice && round?.practice) return 'practice';
    return 'playing';
  });
  const [submitError, setSubmitError] = useState(() => (initialPendingRounds.length ? 'Kaydedilmemiş tamamlanmış bir görev bulundu.' : ''));
  const activeStartedAtRef = useRef(null);
  const elapsedBeforeActiveRef = useRef(
    canResumeStoredRound ? Math.min(durationMs, Number(stored?.elapsed_ms || 0)) : 0,
  );
  const advanceTimerRef = useRef(null);
  const timeoutTriggeredRef = useRef(false);
  const timeoutHandlerRef = useRef(null);

  const persist = (overrides = {}) => {
    writePatternRecognitionProgress(round.round_id, {
      board_session_id: boardSessionId,
      phase,
      question_index: questionIndex,
      responses,
      pending_rounds: pendingRounds,
      practice_completed: practiceCompleted,
      elapsed_ms: elapsedBeforeActiveRef.current,
      ...overrides,
    });
  };

  const startActiveClock = (baseElapsed = elapsedBeforeActiveRef.current) => {
    elapsedBeforeActiveRef.current = Math.min(durationMs, Math.max(0, baseElapsed));
    setElapsedMs(elapsedBeforeActiveRef.current);
    activeStartedAtRef.current = Date.now();
    timeoutTriggeredRef.current = false;
  };

  const currentActiveElapsed = useCallback(() => Math.min(
    durationMs,
    elapsedBeforeActiveRef.current + (activeStartedAtRef.current ? Date.now() - activeStartedAtRef.current : 0),
  ), [durationMs]);

  const submit = async (roundResults) => {
    setPendingRounds(roundResults);
    persist({ pending_rounds: roundResults, phase: 'submitting' });
    setPhase('submitting');
    setSubmitError('');
    try {
      await onComplete(roundResults);
      clearPatternRecognitionProgress(round.round_id);
    } catch (error) {
      setSubmitError(error?.message || 'Sonuç kaydedilemedi.');
      setPhase('submit_error');
    }
  };

  const finishRound = (rawResponses, roundElapsedMs, finishReason) => {
    if (phase !== 'playing') return;
    const completeResponses = normalizedResponses(questions, rawResponses);
    const roundResult = {
      round_number: 1,
      elapsed_ms: Math.min(durationMs, Math.max(0, Math.round(roundElapsedMs))),
      finish_reason: finishReason,
      responses: serverResponses(completeResponses),
    };
    submit([roundResult]);
  };

  timeoutHandlerRef.current = () => {
    if (timeoutTriggeredRef.current || phase !== 'playing' || locked) return;
    timeoutTriggeredRef.current = true;
    elapsedBeforeActiveRef.current = durationMs;
    activeStartedAtRef.current = null;
    setElapsedMs(durationMs);
    setLocked(true);
    setAnnouncement('Süre doldu. Cevaplanmayan sorular boş bırakıldı.');
    finishRound(responses, durationMs, 'timer');
  };

  useEffect(() => {
    if (phase !== 'playing' || locked) return undefined;
    if (activeStartedAtRef.current === null) activeStartedAtRef.current = Date.now();
    const intervalId = window.setInterval(() => {
      const nextElapsed = currentActiveElapsed();
      setElapsedMs(nextElapsed);
      if (nextElapsed >= durationMs) timeoutHandlerRef.current?.();
    }, 100);
    return () => window.clearInterval(intervalId);
  }, [currentActiveElapsed, durationMs, locked, phase]);

  useEffect(() => () => window.clearTimeout(advanceTimerRef.current), []);

  const handleAttempt = (attemptInput) => {
    if (locked) return;
    const question = phase === 'practice' ? round.practice : questions[questionIndex];
    if (!question) return;
    const answerElapsedMs = phase === 'practice' ? 0 : currentActiveElapsed();
    if (phase !== 'practice' && answerElapsedMs >= durationMs) {
      timeoutHandlerRef.current?.();
      return;
    }
    elapsedBeforeActiveRef.current = answerElapsedMs;
    activeStartedAtRef.current = null;
    setElapsedMs(answerElapsedMs);
    const attempt = { ...attemptInput, elapsed_ms: Math.round(answerElapsedMs) };
    const correct = isPatternAttemptCorrect(question, attempt);
    const decoratedAttempt = { ...attempt, correct };
    playMiniTaskSound(correct ? 'correct' : 'wrong', soundEnabled);
    setAnnouncement(correct ? 'Doğru! Deseni yakaladın.' : mirrorGridInPrompt(question.prompt)
      ? `Yanlış. Doğru cevap: ${question.correct_choice_id.toUpperCase()} seçeneği.`
      : 'Doğru cevap yeşil renkle gösterildi.');
    setLocked(true);

    if (phase === 'practice') {
      setPracticeAttempts([decoratedAttempt]);
      advanceTimerRef.current = window.setTimeout(() => {
        setPracticeCompleted(true);
        setPracticeAttempts([]);
        setLocked(false);
        setAnnouncement('');
        setPhase('playing');
        persist({ phase: 'playing', practice_completed: true, elapsed_ms: 0 });
        startActiveClock(0);
      }, NEXT_QUESTION_DELAY_MS);
      return;
    }

    const nextResponses = [...responses];
    nextResponses[questionIndex] = {
      question_id: question.question_id,
      attempts: [decoratedAttempt],
    };
    setResponses(nextResponses);
    persist({ responses: nextResponses, elapsed_ms: answerElapsedMs });
    advanceTimerRef.current = window.setTimeout(() => {
      if (questionIndex >= questions.length - 1) {
        finishRound(nextResponses, answerElapsedMs, 'completed');
        return;
      }
      const nextIndex = questionIndex + 1;
      setQuestionIndex(nextIndex);
      setLocked(false);
      setAnnouncement('');
      persist({ question_index: nextIndex, responses: nextResponses, elapsed_ms: answerElapsedMs });
      startActiveClock(answerElapsedMs);
    }, NEXT_QUESTION_DELAY_MS);
  };

  const activeQuestion = phase === 'practice' ? round.practice : questions[questionIndex];
  const activeAttempts = phase === 'practice' ? practiceAttempts : (responses[questionIndex]?.attempts || []);
  const remainingSeconds = Math.max(0, Math.ceil((durationMs - elapsedMs) / 1000));
  const studentName = round?.student?.display_name || 'Öğrenci';

  if (phase === 'submitting' || phase === 'submit_error') {
    return (
      <Box sx={pageSx}>
        <GardenBackdrop />
        <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
          <Box sx={{ width: '100%', maxWidth: 620, textAlign: 'center', p: 4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.94)', border: '2px solid #e9d5ff', boxShadow: '0 15px 35px rgba(76,29,149,0.14)' }}>
            <Typography component="h1" sx={{ fontWeight: 1000, color: '#251777', fontSize: 30 }}>{phase === 'submitting' ? 'Sonucun kaydediliyor…' : 'Sonuç kaydedilemedi'}</Typography>
            {submitError ? <Typography sx={{ mt: 1, color: '#b91c1c', fontWeight: 700 }}>{submitError}</Typography> : null}
            {phase === 'submit_error' ? (
              <Button startIcon={<ReplayRoundedIcon />} variant="contained" onClick={() => submit(pendingRounds)} sx={{ mt: 3, borderRadius: 99, bgcolor: '#7c3aed', textTransform: 'none', fontWeight: 900 }}>
                Tekrar dene
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      {round?.preview ? (
        <Box sx={{ position: 'fixed', bottom: 12, left: 12, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.5, py: 0.5, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>TEST MODU</Box>
      ) : null}
      <GardenBackdrop />
      <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', width: '100%', maxWidth: 1720, boxSizing: 'border-box', px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 2, md: 3 } }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '220px 1fr 220px' }, alignItems: 'center', gap: { xs: 1.2, md: 2 }, mt: { xs: 2, md: 4 }, mb: { xs: 3.2, sm: 1.5, md: 2.4 } }}>
          <ButtonBase
            onClick={onExit}
            sx={{
              justifySelf: { xs: 'start', md: 'center' }, gridColumn: { xs: '1', md: 'auto' }, gridRow: { xs: '1', md: 'auto' },
              borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.86)', color: '#33258a',
              px: { xs: 1.5, md: 2.2 }, py: { xs: 0.9, md: 1.2 }, minWidth: { xs: 146, md: 210 }, minHeight: { xs: 52, md: 60 },
              boxShadow: '0 4px 0 rgba(91,33,182,0.10)', display: 'flex', alignItems: 'center', gap: 1,
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 25, md: 31 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 25 }, lineHeight: 1 }}>Ana sayfa</Typography>
          </ButtonBase>

          <Typography sx={{ textAlign: 'center', gridColumn: { xs: '1 / -1', md: 'auto' }, gridRow: { xs: '2', md: 'auto' }, color: '#251777', fontWeight: 1000, fontSize: { xs: 28, sm: 42, md: 54 }, lineHeight: 1, overflowWrap: 'anywhere', textShadow: '0 3px 0 rgba(255,255,255,0.94)' }}>
            {withTurkishGenitive(studentName)} Mini Görevi
          </Typography>

          <Box sx={{ justifySelf: { xs: 'end', md: 'center' }, gridColumn: { xs: '2', md: 'auto' }, gridRow: { xs: '1', md: 'auto' }, borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.86)', color: '#33258a', px: { xs: 1.5, md: 2.2 }, py: { xs: 0.9, md: 1.2 }, minWidth: { xs: 118, md: 210 }, textAlign: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 25 }, lineHeight: 1 }}>
              {phase === 'practice' ? 'Alıştırma' : `Soru ${questionIndex + 1} / ${Math.max(1, questions.length)}`}
            </Typography>
            <Typography role="timer" sx={{ mt: 0.45, fontWeight: 900, fontSize: { xs: 14, md: 18 }, color: remainingSeconds <= 10 && phase !== 'practice' ? '#dc2626' : 'inherit' }}>
              {phase === 'practice' ? 'Hazırlık' : `Süre: ${remainingSeconds} sn`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', maxWidth: { xs: 'calc(100vw - 20px)', sm: 1260 }, boxSizing: 'border-box', mx: 'auto', position: 'relative', borderRadius: 4, bgcolor: 'rgba(255,255,255,0.94)', border: '2px solid #e9d5ff', boxShadow: '0 15px 35px rgba(76,29,149,0.14), inset 0 0 0 2px rgba(255,255,255,0.84)', px: { xs: 1.4, sm: 2.4, md: 5 }, pt: { xs: 4.5, md: 5.6 }, pb: { xs: 1.6, md: 2.4 } }}>
          <Box sx={{ position: 'absolute', left: '50%', top: 0, transform: 'translate(-50%, -50%)', borderRadius: 2.5, border: '2px solid #d8b4fe', bgcolor: '#f5f0ff', color: '#6d45df', px: { xs: 1.4, md: 2.2 }, py: { xs: 0.55, md: 0.75 }, display: 'flex', alignItems: 'center', gap: 0.8, minWidth: { xs: 190, md: 270 }, justifyContent: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 25, md: 31 }, filter: 'drop-shadow(0 2px 0 #f59e0b)' }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 23 }, lineHeight: 1 }}>Deseni Yakala</Typography>
          </Box>
          {!mirrorGridInPrompt(activeQuestion?.prompt) ? <>
            <Box aria-hidden="true" sx={{ position: 'absolute', left: { xs: 18, md: 72 }, top: { xs: 54, md: 58 }, color: '#a78bfa', fontSize: 26, fontWeight: 1000 }}>✦</Box>
            <Box aria-hidden="true" sx={{ position: 'absolute', right: { xs: 18, md: 72 }, top: { xs: 64, md: 70 }, color: '#93c5fd', fontSize: 20, fontWeight: 1000 }}>✦</Box>
          </> : null}

          {activeQuestion ? (
            <QuestionCard
              question={activeQuestion}
              attempts={activeAttempts}
              locked={locked}
              revealAnswer={locked}
              onChoice={(choiceId) => handleAttempt({ choice_id: choiceId })}
              onTarget={(targetIndex) => handleAttempt({ target_index: targetIndex })}
            />
          ) : null}

          <Box aria-live="polite" sx={{ minHeight: { xs: 68, md: 84 }, mt: 1.4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, textAlign: 'center', fontWeight: 900, color: announcement ? (announcement.startsWith('Doğru!') ? '#15803d' : '#b91c1c') : '#37306f', fontSize: { xs: 16, md: 20 } }}>
            {!announcement ? <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#a78bfa', color: '#fff', display: 'grid', placeItems: 'center', boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.12)' }}>♥</Box> : null}
            {announcement || 'Cevabını seç ve devam et.'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
